import type { Uuid } from './common';

// --- Teacher portal (OpenAPI: /teacher-portal/*) ---

export interface LessonPlanSummary {
  id: Uuid;
  class_id: Uuid;
  subject_id: Uuid;
  staff_id: Uuid;
  plan_date: string;
  topic: string;
  objectives: string;
  activities: string;
  homework: string;
  status: string;
}

export interface ListLessonPlansResponse {
  lesson_plans: LessonPlanSummary[];
}

export interface CreateLessonPlanRequest {
  class_id: Uuid;
  subject_id: Uuid;
  plan_date: string;
  topic: string;
  objectives?: string | null;
  activities?: string | null;
  homework?: string | null;
  status?: string | null;
}

export interface CreateLessonPlanResponse {
  lesson_plan_id: Uuid;
}

export interface ClassDiarySummary {
  id: Uuid;
  class_id: Uuid;
  staff_id: Uuid;
  entry_date: string;
  title: string;
  notes: string;
  homework: string | null;
  created_at: string;
}

export interface ListClassDiaryResponse {
  diaries: ClassDiarySummary[];
}

export interface CreateClassDiaryRequest {
  class_id: Uuid;
  entry_date: string;
  title: string;
  notes: string;
  homework?: string | null;
}

export interface CreateClassDiaryResponse {
  diary_id: Uuid;
}

/** How attendance was last recorded. */
export type AttendanceSource = 'manual' | 'rfid' | 'admin';

export interface QuickAttendanceEntry {
  student_id: Uuid;
  status: string;
  /** Defaults to `manual` on the server when omitted. */
  source?: AttendanceSource | string | null;
  /** Required by API when overriding an RFID-marked status. */
  reason?: string | null;
}

export interface QuickAttendanceRequest {
  class_id: Uuid;
  date: string;
  section_id?: Uuid | null;
  entries: QuickAttendanceEntry[];
}

export interface QuickAttendanceResponse {
  updated_count: number;
}

export interface ClassAttendanceStudentRow {
  student_id: Uuid;
  admission_no: string;
  first_name: string;
  last_name: string;
  section_id?: Uuid | null;
  section_name?: string | null;
  status?: string | null;
  attendance_id?: Uuid | null;
  source?: string | null;
  marked_by_user_id?: Uuid | null;
  marked_by_email?: string | null;
  notes?: string | null;
}

export interface ClassAttendanceSummaryCounts {
  present: number;
  absent: number;
  late: number;
  excused: number;
  unmarked: number;
}

export interface ClassAttendanceSheetResponse {
  class_id: Uuid;
  class_name: string;
  date: string;
  section_id?: Uuid | null;
  section_name?: string | null;
  total_students: number;
  summary: ClassAttendanceSummaryCounts;
  students: ClassAttendanceStudentRow[];
  page: number;
  per_page: number;
  total_pages: number;
  smart_card_enabled: boolean;
}

export interface AttendanceAuditSummary {
  id: Uuid;
  attendance_id?: Uuid | null;
  student_id: Uuid;
  student_name?: string | null;
  admission_no?: string | null;
  class_id?: Uuid | null;
  class_name?: string | null;
  attendance_date: string;
  actor_user_id?: Uuid | null;
  actor_email?: string | null;
  action: string;
  old_status?: string | null;
  new_status?: string | null;
  source?: string | null;
  reason?: string | null;
  created_at: string;
}

export interface ListAttendanceAuditsResponse {
  audits: AttendanceAuditSummary[];
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
}

export interface QuickMarksEntry {
  student_id: Uuid;
  attended_exam?: boolean;
  marks?: number | null;
}

export interface QuickMarksRequest {
  exam_subject_id: Uuid;
  entries: QuickMarksEntry[];
}

export interface QuickMarksResponse {
  updated_count: number;
}

export interface TeacherClassSummary {
  id: Uuid;
  name: string;
}

export interface ListTeacherClassesResponse {
  classes: TeacherClassSummary[];
}

export interface TeacherExamSummary {
  id: Uuid;
  name: string;
  academic_year: string;
}

export interface ListTeacherExamsResponse {
  exams: TeacherExamSummary[];
}

export interface TeacherExamSubjectSummary {
  id: Uuid;
  exam_id: Uuid;
  subject_id: Uuid;
  subject_name: string;
  max_marks: number;
}

export interface ListTeacherExamSubjectsResponse {
  exam_subjects: TeacherExamSubjectSummary[];
}

export interface TeacherMarksSheetRow {
  student_id: Uuid;
  admission_no: string;
  first_name: string;
  last_name: string;
  section_name?: string | null;
  attended_exam: boolean;
  marks?: number | null;
  result_id?: Uuid | null;
}

export interface TeacherMarksClassSheetResponse {
  class_id: Uuid;
  class_name: string;
  exam_subject_id: Uuid;
  subject_name: string;
  max_marks: number;
  section_id?: Uuid | null;
  section_name?: string | null;
  students: TeacherMarksSheetRow[];
}

export interface TeacherReportCardSummary {
  id: Uuid;
  student_id: Uuid;
  admission_no: string;
  student_name: string;
  term: string;
  academic_year: string;
  percentage?: number | null;
  grade_letter?: string | null;
}

export interface ListTeacherReportCardsResponse {
  report_cards: TeacherReportCardSummary[];
}

/** Roles that count as “teaching staff” for the Teachers directory (subset of /staff). */
export function isTeachingStaffRole(role: string | null | undefined): boolean {
  const r = (role ?? '').trim().toLowerCase();
  if (!r) return false;
  const keys = [
    'teacher',
    'faculty',
    'tutor',
    'instructor',
    'lecturer',
    'coach',
    'hod',
    'head',
    'principal',
    'vice',
  ];
  return keys.some((k) => r.includes(k));
}

export interface Teacher {
  id: string;
  name: string;
  email: string;
  subject: string;
  department: string;
}

/** Backend `school::ClassSummary` — grade/semester slot within an academic program. */
export interface ClassSummary {
  id: Uuid;
  name: string;
  /** Stable cross-tenant class identifier used by global fee templates. */
  class_key?: string | null;
  description?: string | null;
  program_id?: Uuid | null;
  program_code?: string | null;
  program_name?: string | null;
  grade_level?: number | null;
  semester?: number | null;
}

export interface ListClassesParams {
  program_id?: string;
  class_key?: string;
  grade_level?: number;
  semester?: number;
  page?: number;
  limit?: number;
  offset?: number;
}

export interface AcademicProgramSummary {
  id: Uuid;
  code: string;
  name: string;
  kind: 'school_board' | 'college_degree' | string;
  max_semesters?: number | null;
  /** `ug` | `pg` | `diploma` | `super_specialty` for college programs. */
  level?: string | null;
  /** `semester` | `professional_year` | `phase`. */
  progression?: string | null;
  includes_internship?: boolean | null;
  terminal_slot_label?: string | null;
  duration_months?: number | null;
  status: string;
}

export interface ListAcademicProgramsResponse {
  programs: AcademicProgramSummary[];
}

export interface CreateAcademicProgramRequest {
  code: string;
  name: string;
  kind: 'school_board' | 'college_degree';
  max_semesters?: number | null;
  level?: string | null;
  progression?: string | null;
  includes_internship?: boolean | null;
  terminal_slot_label?: string | null;
  duration_months?: number | null;
}

export interface CreateAcademicProgramResponse {
  program_id: Uuid;
  classes_created: number;
}

export interface ListClassesResponse {
  classes: ClassSummary[];
  total?: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

export interface CreateClassRequest {
  name: string;
  description?: string | null;
  class_key?: string | null;
}

export interface CreateClassResponse {
  class_id: Uuid;
}

export interface UpdateClassRequest {
  class_id: Uuid;
  name: string;
  description?: string | null;
  status: string;
  class_key?: string | null;
}

export interface SoftDeleteClassRequest {
  class_id: Uuid;
}

/** Backend `school::SubjectSummary` */
export interface SubjectSummary {
  id: Uuid;
  class_id: Uuid;
  name: string;
  subject_code?: string | null;
  /** core | elective | optional */
  selection_type?: string;
  /** theory | lab | practical | activity | skill */
  subject_kind?: string;
  /** cbse | icse */
  board_code?: string | null;
}

export interface ListSubjectsResponse {
  subjects: SubjectSummary[];
  total?: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

export interface ListSubjectsQuery {
  class_id?: string;
  selection_type?: string;
  page?: number;
  limit?: number;
}

export interface BoardSubjectDefaultSummary {
  id: string;
  board_code: string;
  name: string;
  subject_code?: string | null;
  subject_kind: string;
  selection_type: string;
  grade_from: number;
  grade_to: number;
  sort_order: number;
}

export interface ListBoardSubjectDefaultsResponse {
  subjects: BoardSubjectDefaultSummary[];
}

export interface SeedBoardSubjectDefaultsResponse {
  board_code: string;
  subjects_created: number;
}

export interface CreateSubjectRequest {
  class_id: Uuid;
  name: string;
  subject_code?: string | null;
  selection_type?: string | null;
  subject_kind?: string | null;
  board_code?: string | null;
}

export interface CreateSubjectResponse {
  subject_id: Uuid;
}

export interface UpdateSubjectRequest {
  subject_id: Uuid;
  name: string;
  status: string;
  subject_code?: string | null;
  selection_type?: string | null;
  subject_kind?: string | null;
  board_code?: string | null;
}

export interface SoftDeleteSubjectRequest {
  subject_id: Uuid;
}

/** Backend subject combination / stream for a class (e.g. PCM, PCB, Arts, Commerce). */
export interface ClassSubjectGroupSummary {
  id: Uuid;
  class_id: Uuid;
  code: string;
  name: string;
  description?: string | null;
  min_electives: number;
  max_electives?: number | null;
  status: string;
  /** Elective subject ids that belong to this combination. */
  subject_ids: Uuid[];
}

export interface ListClassSubjectGroupsResponse {
  groups: ClassSubjectGroupSummary[];
  total?: number;
  page?: number;
  per_page?: number;
}

export interface CreateClassSubjectGroupRequest {
  class_id: Uuid;
  code: string;
  name: string;
  description?: string | null;
  min_electives?: number | null;
  max_electives?: number | null;
  /** Elective subjects in this combination (e.g. PCM → Physics, Chemistry, Math). */
  subject_ids: Uuid[];
}

export interface CreateClassSubjectGroupResponse {
  group_id: Uuid;
}

export interface BulkClassSubjectItem {
  name: string;
  subject_code?: string | null;
  selection_type?: string | null;
  subject_kind?: string | null;
  board_code?: string | null;
}

export interface BulkCreateClassSubjectsRequest {
  class_id: Uuid;
  subjects: BulkClassSubjectItem[];
}

export interface BulkCreateClassSubjectsResponse {
  subject_ids: Uuid[];
}

/** GET `/api/school` — matches Rust `ApiSchoolResponse`. */
export interface SchoolProfileJson {
  id?: string | number;
  tenant_sequence?: number | null;
  name?: string;
  org_code?: string | null;
  org_type?: string;
  status?: string;
  country_code?: string | null;
  currency_code?: string;
  board_code?: string;
  logo_url?: string | null;
  logo_media_id?: string | null;
  affiliation_board?: string | null;
  affiliation_no?: string | null;
  affiliation_number?: string | null;
  affiliated_for?: string | null;
  affiliation_category?: string | null;
  affiliation_valid_until?: string | null;
  contact_numbers?: string | null;
  show_school_profile_on_documents?: boolean;
  portal_report_card_years_visible?: number;
  report_card_purge_after_years?: number;
  portal_admit_card_years_visible?: number;
  admit_card_purge_after_years?: number;
  portal_fee_paid_years_visible?: number;
  fee_paid_purge_after_years?: number;
  /**
   * Day of month (1–28) when monthly fee invoices should be generated.
   * Capped at 28 so February always has a valid day.
   */
  fee_invoice_generation_day?: number;
  /** Day of month (1–28) used as the default due date on generated invoices. */
  fee_invoice_due_day?: number;
  /**
   * When true (default), invoices are generated automatically on/after
   * `fee_invoice_generation_day` for the current billing period.
   */
  fee_auto_generate_invoices?: boolean;
  parent_tenant_id?: number | null;
  parent_tenant_name?: string | null;
  sub_tenant_count?: number;
  /** `day_scholar` | `resident` | `both` — campus student mix from super-admin provisioning. */
  student_residence_mode?: string | null;
  [key: string]: unknown;
}

export interface SchoolBranchSummary {
  id: number;
  name: string;
  is_headquarters?: boolean;
  is_current?: boolean;
  org_type?: string | null;
  org_code?: string | null;
  status?: string | null;
  tenant_sequence?: number | null;
}

/** GET `/api/school/branches` — `{ branches: SchoolBranchSummary[] }` or legacy shapes. */
export type SchoolBranchesJson =
  | { branches: SchoolBranchSummary[] }
  | Record<string, unknown>
  | unknown[];

/** GET `/tenants/me` — tenant board & currency for UI formatting. */
export interface TenantSettingsResponse {
  tenant_id: number;
  tenant_sequence?: number | null;
  name: string;
  org_code?: string | null;
  org_type: string;
  board_code: string;
  country_code?: string | null;
  currency_code: string;
  currency_symbol: string;
  logo_url?: string | null;
  logo_media_id?: string | null;
  affiliation_board?: string | null;
  affiliation_no?: string | null;
  affiliation_number?: string | null;
  affiliated_for?: string | null;
  affiliation_category?: string | null;
  affiliation_valid_until?: string | null;
  contact_numbers?: string | null;
  /** `day_scholar` | `resident` | `both` — controls hostel vs day-scholar UI. */
  student_residence_mode?: string | null;
}

export interface UpdateSchoolProfileRequest {
  name?: string;
  org_code?: string | null;
  org_type?: string;
  status?: string;
  board_code?: string;
  country_code?: string | null;
  currency_code?: string;
  logo_url?: string | null;
  logo_media_id?: string | null;
  affiliation_board?: string | null;
  affiliation_no?: string | null;
  affiliated_for?: string | null;
  affiliation_category?: string | null;
  affiliation_valid_until?: string | null;
  contact_numbers?: string | null;
  show_school_profile_on_documents?: boolean;
  portal_report_card_years_visible?: number;
  report_card_purge_after_years?: number;
  portal_admit_card_years_visible?: number;
  admit_card_purge_after_years?: number;
  portal_fee_paid_years_visible?: number;
  fee_paid_purge_after_years?: number;
  fee_invoice_generation_day?: number;
  fee_invoice_due_day?: number;
  fee_auto_generate_invoices?: boolean;
  student_residence_mode?: string | null;
}

export interface SeedReportCardBoardDefaultsResponse {
  board_code: string;
  grading_rules_seeded: boolean;
  templates_seeded: boolean;
}

/** Matches OpenAPI `AcademicYearSummary` (also returned under academic-planning). */
export interface AcademicYearSummary {
  id: Uuid;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  is_current: boolean;
}

export interface ListAcademicYearsResponse {
  academic_years: AcademicYearSummary[];
}

export interface CreateAcademicYearRequest {
  name: string;
  start_date: string;
  end_date: string;
  is_current?: boolean | null;
  status?: string | null;
}

export interface CreateAcademicYearResponse {
  academic_year_id: Uuid;
}
